import {noop} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import AnalysisGraphUtils from 'analysis-tool/flowchart/analysis-graph/AnalysisGraphUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import FlowchartNodeToolbar, {
  FlowchartNodeToolbarButton,
} from 'ui-library/reactflow/nodes/toolbar/FlowchartNodeToolbar';

import {AnalysisFlowchartAction} from '../actions/AnalysisFlowchartActionTypes';
import {GetActionInfoFn} from '../actions/createGetActionInfoFn';
import {useAnalysisFlowchartContext} from '../AnalysisFlowchartContext';

interface ActionTitleAndIcon {
  singularTitle: string;
  pluralTitle: string;
  icon: BenchlingIconName;
}

const SUPPORTED_ACTIONS = [
  AnalysisFlowchartAction.DUPLICATE_STEPS,
  AnalysisFlowchartAction.COPY_TO_CLIPBOARD,
  AnalysisFlowchartAction.RERUN_STEPS,
  AnalysisFlowchartAction.DELETE_STEPS,
] as const;

/** @package Exported for tests */
export const ACTION_TITLE_AND_ICON = {
  [AnalysisFlowchartAction.DUPLICATE_STEPS]: {
    singularTitle: 'Duplicate step',
    pluralTitle: 'Duplicate steps',
    icon: 'copy',
  },
  [AnalysisFlowchartAction.COPY_TO_CLIPBOARD]: {
    singularTitle: 'Copy step to clipboard',
    pluralTitle: 'Copy steps to clipboard',
    icon: 'clipboard-list',
  },
  [AnalysisFlowchartAction.RERUN_STEPS]: {
    singularTitle: 'Refresh step',
    pluralTitle: 'Refresh steps',
    icon: 'refresh',
  },
  [AnalysisFlowchartAction.DELETE_STEPS]: {
    singularTitle: 'Delete step',
    pluralTitle: 'Delete steps',
    icon: 'delete',
  },
} satisfies Record<(typeof SUPPORTED_ACTIONS)[number], ActionTitleAndIcon>;

function getToolbarButton(
  action: (typeof SUPPORTED_ACTIONS)[number],
  stepIds: ReadonlyArray<string>,
  getActionInfo: GetActionInfoFn
): FlowchartNodeToolbarButton | null {
  const {singularTitle, pluralTitle, icon} = ACTION_TITLE_AND_ICON[action];
  const title = stepIds.length > 1 ? pluralTitle : singularTitle;

  const actionInfo = getActionInfo(action, ...stepIds);

  // If the action is not enabled and does not have a user-facing disabled message, it should be hidden.
  if (!actionInfo.enabled && actionInfo.message == null) {
    return null;
  }

  return {
    icon,
    elementName: action,
    disabled: !actionInfo.enabled || undefined,
    tooltip: actionInfo.enabled ? title : actionInfo.message!,
    onClick: actionInfo.enabled ? () => actionInfo.doAction(...stepIds) : noop,
  };
}

function getToolbarButtons(
  stepIds: ReadonlyArray<string>,
  getActionInfo: GetActionInfoFn
): Array<FlowchartNodeToolbarButton> {
  return SUPPORTED_ACTIONS.map((action) => getToolbarButton(action, stepIds, getActionInfo)).filter(
    (button) => button != null
  );
}

export default function AnalysisFlowchartToolbar(): JSX.Element | null {
  const {getActionInfo, selectedNodeIds} = useAnalysisFlowchartContext();
  const selectedStepIds = useMemo(
    () => [...selectedNodeIds].map((nodeId) => AnalysisGraphUtils.getStepIdFromNodeId(nodeId)),
    [selectedNodeIds]
  );
  const buttons = useMemo(
    () => getToolbarButtons(selectedStepIds, getActionInfo),
    [selectedStepIds, getActionInfo]
  );

  if (selectedNodeIds.size === 0 || buttons.length === 0) {
    return null;
  }

  return <FlowchartNodeToolbar nodeIds={[...selectedNodeIds]} buttons={buttons} />;
}
